import {MaterialSelect, MaterialSelectOptions, OptionData} from "../../components/material-select";

export class PanelForm {
    private _content: HTMLElement;
    private _parent: HTMLElement;
    private _id: string | number | undefined;
    protected _selectElements: Map<string, MaterialSelect<any>> = new Map();
    private _selectsInitialized: boolean = false;

    set id(value: string | number | undefined) {
        this._id = value;
    }

    get id(): string | number | undefined {
        if(!this._id){
            const input = this.getInput('id');
            if(input){
                this._id = input.value;
            }else{
                console.warn('ID not set and no input with name "id" found');
            }
        }
        return this._id;
    }

    constructor(element : HTMLDivElement | undefined | string | null){
        let foundElement;
        if(typeof element === 'string'){
            foundElement = document.getElementById(element);
            if (!foundElement) {
                throw new Error(`Element with id ${element} not found`);
            }
        } else if (element instanceof HTMLDivElement) {
            foundElement = element;
        } else {
            console.warn('Element not found', new Error().stack);
            // Create and append to document if needed
            foundElement = document.createElement('div');
            document.body.appendChild(foundElement); // Or append to specific container
        }

        if(foundElement.classList.contains('modal')){
            this._content = (foundElement as HTMLDivElement).querySelector('.modal-content') as HTMLDivElement;
        }else {
            this._content = foundElement as HTMLDivElement;
        }
        this._parent = foundElement;
    }

    public initSelectElements(){
        // If we've already initialized and have instances, skip unless the gate was reset
        if (this._selectsInitialized && this._selectElements.size > 0) {
            return;
        }
        const selects = this._content.querySelectorAll('select');
        selects.forEach(select => {
            const selectId = select.name || select.id;

            // Check if we already have an instance for this select
            const existingInstance = this._selectElements.get(selectId);

            if (!existingInstance || existingInstance.isDestroyed) {
                // Only create a new instance if one doesn't exist or was destroyed
                const instance = MaterialSelect.init(select);
                console.debug('Initialized MaterialSelect for', selectId, instance);
                if (instance) {
                    this._selectElements.set(selectId, instance);
                }
            } else {
                console.debug('Reusing existing MaterialSelect instance for', selectId);
            }
        });

        // Close the gate. This will never run again unless explicitly reset.
        this._selectsInitialized = true;
    }


    public set record(data: Record<string, any>) {
        for (const key in data) {
            // 1. Find the DOM element
            const element = this._content.querySelector<HTMLElement>(`[name="${key}"]`);

            if (element) {
                // Handle Complex Objects (Autocomplete)
                if (typeof data[key] === 'object' && data[key] !== null) {
                    if (element instanceof HTMLInputElement && element.classList.contains('autocomplete')) {
                        const autocomplete = element as HTMLInputElement;
                        const id = data[key].id;
                        const value = data[key].text; // Ensure 'text' exists in your data contract
                        autocomplete.dataset.id = id;
                        autocomplete.value = value;
                    }
                }
                // Handle Multi-Select Native
                else if (element instanceof HTMLSelectElement && element.multiple) {
                    const values = data[key] as string[];
                    const options = element.querySelectorAll('option');
                    options.forEach(option => {
                        option.selected = values.includes(option.value);//TODO: THIS DOESNT WORK
                    });
                }
                // Handle Custom Select Wrappers
                else if (element instanceof HTMLSelectElement) {
                    // Assuming _selectElements is a Map<string, CustomComponent>
                    const select = this._selectElements.get(key);

                    // If a custom wrapper exists, use it
                    if (select) {
                        select.selected = data[key];
                    } else {
                        // Fallback: if no wrapper, set the native element value
                        element.value = data[key];
                    }
                }
                // Handle Checkboxes
                else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
                    element.checked = data[key];
                }
                // Handle Standard Inputs
                else if(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
                    element.value = data[key];
                }
            }

            // REMOVED THE ERRONEOUS LOOP HERE
        }
        this._id = data.id;
    }

    protected updateRecordFromSelect(data: Record<string, any>, select: MaterialSelect, key: string) {
        const element = document.getElementById(select.elementId) as HTMLSelectElement;
        if(!element) return;
        if (!data.hasOwnProperty(key)) {
            if(element.dataset.id !== undefined){
                data[key] = {
                    id: select.selected,
                };
            }else{
                data[key] = select.selected;
            }
        }
    }

    protected updateRecordFromAutocomplete(data: Record<string, any>, input: HTMLInputElement, key: string) {
        if (!data.hasOwnProperty(key)) {
            data[key] = {
                id: input.dataset.id,
                value: input.value
            };
        }
    }

    protected updateRecordFromDataIdInput(data: Record<string, any>, element: HTMLInputElement, key: string) {
        if (!data.hasOwnProperty(key)) {
            data[key] = {
                id: element.dataset.id,
                value: element.value
            }
        }
    }

    public get record(): Record<string, any> {
        const form = this.form;
        const formData = new FormData(form);
        const data: Record<string, any> = {};
        formData.forEach((value, key) => {
            const element = form.querySelector<HTMLInputElement>(`[name="${key}"]`);
            if (element instanceof HTMLSelectElement && element.multiple) {
                data[key] = Array.from(element.selectedOptions).map(option => option.value);
            } else if(element){
                if(element.classList.contains('autocomplete')){
                    this.updateRecordFromAutocomplete(data, element, key);
                }else if(element.dataset.id !== undefined){
                    this.updateRecordFromDataIdInput(data, element, key);
                }else {
                    data[key] = value.toString();
                }
            }
        });
        // Include MaterialSelect values
        this._selectElements.forEach((select, name) => {
            this.updateRecordFromSelect(data, select, name);
        });
        // Only override the form's own id when this instance actually carries one. `_id` is set by
        // the record setter, which a server-rendered page never calls — and a subclass that
        // overrides the `id` accessor (job-card details) never writes it either. Assigning it
        // unconditionally therefore wiped the hidden `id` input out of the payload, and every save
        // of an existing record reached the API with no id and was persisted as a brand new row.
        data.id = this._id ?? data.id;
        return data;
    }

    protected get parent(): HTMLElement {
        return this._parent;
    }

    get contentElement(): HTMLElement {
        return this._content;
    }

    set contentElement(value: HTMLElement){
        this._content = value;
    }

    get content(): string {
        return this._content.innerHTML;
    }

    set content(value: string){
        this._content.innerHTML = value;
    }

    get isValid(): boolean {
        let valid = true;
        this._content.querySelectorAll('input').forEach(input => {
            if(input.required && !input.value){
                valid = false;
                input.classList.add('invalid');
            }else{
                input.classList.remove('invalid');
            }
        });
        this._selectElements.forEach((materialSelect, name) => {
            const selectElement = document.getElementById(materialSelect.elementId) as HTMLSelectElement;
            const closestInput = selectElement.parentElement?.querySelector('input');
            if(selectElement.required && !materialSelect.selected){
                valid = false;
                selectElement.classList.add('invalid');
                if(closestInput) closestInput.classList.add('invalid');
            }else{
                selectElement.classList.remove('invalid');
                if(closestInput) closestInput.classList.remove('invalid');
            }
        });
        this._content.querySelectorAll('textarea').forEach(textarea => {
            if(textarea.required && !textarea.value){
                valid = false;
                textarea.classList.add('invalid');
            }else{
                textarea.classList.remove('invalid');
            }
        });
        return valid;
    }

    public getForm(id?: string): HTMLFormElement | null {
        if(!id){
            return this.form;
        }
        return this._content.querySelector<HTMLFormElement>(`#${id}`);
    }

    get form(){
        const form = this._content.querySelector('form') as HTMLFormElement;
        if(!form) throw new Error('Form not found');
        return form;
    }

    public getElement<T extends HTMLElement>(selector: string): T | null {
        return this._content.querySelector<T>(selector);
    }

    public getInput(name: string): HTMLInputElement | null {
        return this._content.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    }

    public getSelect<T extends OptionData>(name: string, options?: MaterialSelectOptions<T>): MaterialSelect | null {
        if(options){
            const selectElement = this._content.querySelector<HTMLSelectElement>(`select[name="${name}"]`);
            if(selectElement && !this._selectElements.has(name)){
                const materialSelect = MaterialSelect.init(selectElement, options);
                if(materialSelect) this._selectElements.set(name, materialSelect);
            }
        }
        return this._selectElements.get(name) || null;
    }

    public getTextArea(name: string): HTMLTextAreaElement | null {
        return this._content.querySelector<HTMLTextAreaElement>(`textarea[name="${name}"]`);
    }

    public getModal(): HTMLElement {
        return this._content;
    }

    public clear(){
        const inputs = this._content.querySelectorAll('input');
        const selects = this._content.querySelectorAll('select');
        const textareas = this._content.querySelectorAll('textarea');
        inputs.forEach(input => input.value = '');
        selects.forEach(select => select.value = '');
        textareas.forEach(textarea => textarea.value = '');
        this._id = undefined;
    }

    public enableResizing(){
        //TODO: implement option logic
        const elem = this._parent;
        if(!elem) return;
        const currentHeight = elem.clientHeight ?? 0;
        const storedHeight = localStorage.getItem('comments-panel-height');
        const minimized = (elem.classList.contains('minimized') ? 32 : currentHeight);
        elem.style.height = storedHeight ?? minimized + 'px';
        elem.style.maxHeight = (currentHeight + 10) + 'px';
        elem.style.minHeight = '32px';
        elem.classList.add('resizable-box');
        //After resized:
        document.addEventListener('mouseup', () => {
            localStorage.setItem('comments-panel-height', elem.clientHeight + 'px');
        });

    }

    public getButtons(): HTMLAnchorElement[] {
        const footer = (this._parent!.querySelector('.modal-footer') as HTMLDivElement);
        if (!footer) {
            console.warn('Footer not found in the modal');
            return [];
        }
        const buttons = footer.querySelectorAll('a');
        if (!buttons || buttons.length === 0) {
            console.warn('No buttons found in the modal footer');
            return [];
        }
        return Array.from(buttons) as HTMLAnchorElement[];
    }

}

export interface ResizeOptions {
    resizeDirections: string;
}
